import { Provider, Type } from '@angular/core';
import { TestBed, waitForAsync } from '@angular/core/testing';
import { By } from '@angular/platform-browser';
import { BrowserDynamicTestingModule } from '@angular/platform-browser-dynamic/testing';

import { setProps } from '../internals/query';
import * as customMatchers from '../matchers';
import { addMatchers } from '../core';
import { isType } from '../types';
import { HostComponent } from '../Spec-host/host-component';
import { BaseSpecOverrides } from '../base/options';
import { nodeByDirective } from '../internals/node-by-directive';

import { initialSpecDirectiveModule } from './initial-module';
import { getSpecDirectiveDefaultOptions, SpecDirectiveOptions } from './options';
import { SpecDirective } from './spec-directive';
import { overrideModules } from '../Spec/create-factory';

/**
 * @publicApi
 */
export type SpecDirectiveFactory<D, H> = <HP>(
  template: string,
  overrides?: SpecDirectiveOverrides<D, H, HP>
) => SpecDirective<D, H & (HostComponent extends H ? HP : unknown)>;

/**
 * @publicApi
 */
export type PresetSpecDirectiveFactory<D, H> = <HP>(
  template?: string,
  overrides?: SpecDirectiveOverrides<D, H, HP>
) => SpecDirective<D, H & (HostComponent extends H ? HP : unknown)>;

/**
 * @publicApi
 */
export interface SpecDirectiveOverrides<D, H, HP> extends BaseSpecOverrides {
  detectChanges?: boolean;
  props?: Partial<D>;
  hostProps?: HostComponent extends H ? HP : Partial<H>;
  directiveProviders?: Provider[];
}

/**
 * @publicApi
 */
export function createDirectiveFactory<D, H = HostComponent>(
  options: SpecDirectiveOptions<D, H> & { template: string }
): PresetSpecDirectiveFactory<D, H>;
/**
 * @publicApi
 */
export function createDirectiveFactory<D, H = HostComponent>(
  typeOrOptions: Type<D> | SpecDirectiveOptions<D, H>
): SpecDirectiveFactory<D, H>;
export function createDirectiveFactory<D, H = HostComponent>(
  typeOrOptions: Type<D> | SpecDirectiveOptions<D, H>
): SpecDirectiveFactory<D, H> {
  const options = isType(typeOrOptions)
    ? getSpecDirectiveDefaultOptions<D, H>({ directive: typeOrOptions })
    : getSpecDirectiveDefaultOptions(typeOrOptions);

  const moduleMetadata = initialSpecDirectiveModule<D, H>(options);

  beforeEach(
    waitForAsync(() => {
      addMatchers(customMatchers);
      TestBed.configureTestingModule(moduleMetadata);
      overrideModules(options);
    })
  );

  return <HP>(template?: string, overrides?: SpecDirectiveOverrides<D, H, HP>) => {
    const defaults: SpecDirectiveOverrides<D, H, HP> = {
      props: {},
      hostProps: {} as any,
      detectChanges: true,
      providers: []
    };
    const { detectChanges, props, hostProps, providers } = { ...defaults, ...overrides };

    if (providers && providers.length) {
      providers.forEach((provider: Provider) => {
        TestBed.overrideProvider((provider as any).provide, provider as any);
      });
    }

    TestBed.overrideModule(BrowserDynamicTestingModule, {
      set: {
        entryComponents: moduleMetadata.entryComponents
      }
    }).overrideComponent(options.host, {
      set: { template: template || options.template }
    });

    if (options.directiveProviders.length || options.directiveMocks.length) {
      TestBed.overrideDirective(options.directive, {
        set: { providers: [...options.directiveProviders, ...options.directiveMocks.map(p => options.mockProvider(p))] }
      });
    }

    const Spec = createSpecDirective(options, props, hostProps);

    if (options.detectChanges && detectChanges) {
      Spec.detectChanges();
    }

    return Spec;
  };
}

function createSpecDirective<D, H, HP>(
  options: Required<SpecDirectiveOptions<D, H>>,
  props?: Partial<D>,
  hostProps?: HP
): SpecDirective<D, H & HP> {
  const hostFixture = TestBed.createComponent(options.host);
  const debugElement = hostFixture.debugElement.query(By.directive(options.directive)) || hostFixture.debugElement;
  const debugNode = hostFixture.debugElement.queryAllNodes(nodeByDirective(options.directive))[0];

  if (!debugNode) {
    throw new Error(`Cannot find directive ${options.directive} in host template 😔`);
  }

  const hostComponent = setProps(hostFixture.componentInstance, hostProps);
  const directive = setProps(debugNode.injector.get(options.directive), props);

  return new SpecDirective(hostComponent, hostFixture, hostFixture.debugElement, directive, debugElement.nativeElement);
}
